import {
  LuUsers,
  LuSearch,
  LuFilter,
  LuLoader,
  LuShield,
  LuUser,
  LuActivity
} from "react-icons/lu";
import React, { useState, useEffect, useMemo } from "react";
import { db } from "@lib/firebase";
import { collection, getDocs, query, orderBy, limit, startAfter, where, getCountFromServer, startAt, endAt } from "firebase/firestore";
import { useAuth } from "../../context/AuthContext";
import { InputField } from "@cakrawalajakarta/shared-components";

export const Users = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [lastVisible, setLastVisible] = useState(null);
  const [isLastPage, setIsLastPage] = useState(false);
  const [itemsPerPage] = useState(50);

  const fetchUsers = async (action = "init") => {
    if (action === "init") {
      setLoading(true);
      setUsers([]);
    }
    try {
      let q = collection(db, "users");
      const constraints = [];

      // Apply role filter
      if (roleFilter !== "all") {
        constraints.push(where("role", "==", roleFilter));
      }

      // Apply search filter
      if (action === "search" && searchQuery) {
          const term = searchQuery.trim();
          
          if (term.includes("@")) {
              // Email Search (exact match)
              constraints.push(where("email", "==", term));
          } else {
              // Name Search (range query) - try displayName first, fallback to name
              const nameTerm = term.charAt(0).toUpperCase() + term.slice(1);
              constraints.push(orderBy("name"));
              constraints.push(startAt(nameTerm));
              constraints.push(endAt(nameTerm + "\uf8ff"));
          }
      } else {
          // Default Sort (Only if not searching)
          constraints.push(orderBy("email", "asc"));
      }

      // Apply pagination
      if (action === "next" && lastVisible) {
        constraints.push(startAfter(lastVisible));
      }
      constraints.push(limit(itemsPerPage));

      q = query(q, ...constraints);

      const querySnapshot = await getDocs(q);
      const usersData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      if (action === "init" || action === "search") {
        setUsers(usersData);
      } else {
        setUsers(prev => [...prev, ...usersData]);
      }

      setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1]);
      setIsLastPage(querySnapshot.docs.length < itemsPerPage);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery !== "") {
        fetchUsers("search");
      } else {
        fetchUsers("init");
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery, roleFilter]);

  const formatDate = (dateField, fallbackField = null) => {
    if (!dateField && fallbackField) {
      dateField = fallbackField;
    }
    if (!dateField) return "-";
    try {
      let date;
      if (dateField._seconds) {
        date = new Date(dateField._seconds * 1000);
      } else if (dateField.toDate && typeof dateField.toDate === 'function') {
        date = dateField.toDate();
      } else if (typeof dateField === 'string') {
        date = new Date(dateField);
      } else if (dateField instanceof Date) {
        date = dateField;
      } else if (typeof dateField === 'number') {
        date = new Date(dateField);
      } else {
        date = new Date(dateField);
      }
      
      if (isNaN(date.getTime())) return "-";
      
      return date.toLocaleDateString('id-ID', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error("Date formatting error:", error, dateField);
      return "-";
    }
  };

  const getRoleBadge = (role) => {
    if (!role) return <span className="px-2 py-1 text-xs font-medium bg-slate-100 text-slate-600 rounded-full">User</span>;
    
    switch (role.toLowerCase()) {
      case "admin":
        return <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">Admin</span>;
      case "superadmin":
        return <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-700 rounded-full">Superadmin</span>;
      case "rpc":
        return <span className="px-2 py-1 text-xs font-medium bg-emerald-100 text-emerald-700 rounded-full">RPC</span>;
      default:
        return <span className="px-2 py-1 text-xs font-medium bg-slate-100 text-slate-600 rounded-full">User</span>;
    }
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <LuLoader className="animate-spin text-4xl text-slate-800" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Dashboard Style */}
      <div className="bg-white rounded-xl p-4 border border-slate-200 text-slate-800 relative overflow-hidden">
         <div className="absolute top-0 right-0 p-4 opacity-5 text-slate-200 pointer-events-none">
            <LuUsers size={120} />
         </div>
         <div className="relative z-10">
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
               <div>
                  <p className="text-slate-500 font-medium mb-1 flex items-center gap-2 text-xs uppercase tracking-wider">
                     <LuActivity size={14} /> Administration
                  </p>
                   <h1 className="text-2xl md:text-3xl font-bold mb-2 break-words">User Management</h1>
                   <p className="text-slate-500 text-sm max-w-xl">
                       Search and manage <strong>JOTR2026</strong> user accounts and roles.
                   </p>
               </div>
            </div>
         </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border border-slate-200">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <InputField
                name="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by email or name..."
                startIcon={<LuSearch className="text-slate-400" />}
                labelClassName="sr-only"
            />
          </div>

          {/* Role Filter */}
          <div className="min-w-[180px]">
            <InputField
                type="select"
                name="roleFilter"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                options={[
                    { value: "all", label: "All Roles" },
                    { value: "admin", label: "Admin" },
                    { value: "superadmin", label: "Superadmin" },
                    { value: "rpc", label: "RPC" },
                    { value: "user", label: "User" },
                ]}
                startIcon={<LuFilter className="text-slate-400" />}
                defaultOptionLabel="Role Status"
                labelClassName="sr-only"
            />
          </div>
        </div>
      </div>

        <p className="text-sm text-slate-500">
          Showing <span className="font-semibold text-slate-700">{users.length}</span> users
        </p>

      {/* Table Header */}
      <div className="bg-white rounded-xl border border-slate-200 border-b-0 overflow-hidden">
         <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
               <thead>
                  <tr className="bg-slate-50 border-b">
                     <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide" style={{width: "33.33%"}}>Name / Email</th>
                     <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide" style={{width: "25%"}}>Role</th>
                     <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide" style={{width: "25%"}}>Created At</th>
                     <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide" style={{width: "16.67%"}}>Last Login</th>
                  </tr>
               </thead>
            </table>
         </div>
      </div>

      {/* User Cards */}
      <div className="space-y-3">
        {users.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <p className="text-slate-500">No users found</p>
          </div>
        ) : (
          users.map((user) => (
            <div key={user.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-4">
                {/* Mobile Card View */}
                <div className="lg:hidden flex flex-col gap-3">
                   {/* Header: Avatar, Name, Email */}
                   <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center text-slate-700 text-lg font-bold flex-shrink-0">
                        {(user.displayName || user.name || user.email || "?").charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                         <div className="flex justify-between items-start">
                            <h3 className="font-bold text-slate-900 text-base line-clamp-1">{user.displayName || user.name || "-"}</h3>
                            {getRoleBadge(user.role)}
                         </div>
                         <p className="text-sm text-slate-500 truncate mb-1">{user.email || "-"}</p>
                      </div>
                   </div>

                   {/* Stats Grid */}
                   <div className="grid grid-cols-2 gap-2 border-t border-b border-slate-50 py-3">
                      <div className="bg-slate-50 rounded p-2 text-center">
                         <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider mb-0.5">Created At</p>
                         <p className="font-bold text-slate-700 text-xs">{formatDate(user.createdAt)}</p>
                      </div>
                      <div className="bg-slate-50 rounded p-2 text-center">
                         <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider mb-0.5">Last Login</p>
                         <p className="font-bold text-slate-700 text-xs">{formatDate(user.lastLogin, user.createdAt)}</p>
                      </div>
                   </div>
                </div>

                {/* Desktop Row View */}
                <div className="hidden lg:grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-slate-700 font-bold flex-shrink-0">
                        {(user.displayName || user.name || user.email || "?").charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-900 truncate">{user.displayName || user.name || "-"}</p>
                        <p className="text-sm text-slate-500 truncate">{user.email || "-"}</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-3">
                    <div className="flex flex-col items-start gap-1">
                      {getRoleBadge(user.role)}
                      <p className="text-[10px] text-slate-400 font-mono truncate">{user.id}</p>
                    </div>
                  </div>
                  <div className="col-span-3 text-sm text-slate-600">
                    {formatDate(user.createdAt)}
                  </div>
                  <div className="col-span-2 text-sm text-slate-600">
                    {formatDate(user.lastLogin, user.createdAt)}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Load More Button */}
      {!isLastPage && (
          <div className="flex justify-end mt-6">
              <button 
                onClick={() => fetchUsers("next")} 
                disabled={loading} 
                className="px-6 py-2 rounded-xl bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50 transition-all flex items-center gap-2"
              >
                 {loading ? <LuLoader className="animate-spin" /> : null}
                 Load More
              </button>
          </div>
      )}
    </div>
  );
};
